import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { RECIPE_TYPE, RESTORATION_METHOD } from './constants'

export type FormData = {
  [key in FORM_FIELDS]: string
}

export const enum FORM_FIELDS {
  RECIPE_TYPE = 'recipe_type',
  RESTORATION_METHOD = 'restoration_method',
  STUMP_SHADE = 'natural_die_material',
  PREPARATION_COLOR = 'prepcolor',
  PREPARATION_COLOR_MIDDLE = 'prepcolor_middle',
  PREPARATION_COLOR_CERVICAL = 'prepcolor_cervical',
  PREPARATION_COLOR_INCISAL = 'prepcolor_incisal',
  TARGET_COLOR = 'targetcolor',
  TARGET_COLOR_VALUE_MIDDLE = 'targetcolor_middle_value',
  TARGET_COLOR_VALUE_CERVICAL = 'targetcolor_cervical_value',
  TARGET_COLOR_VALUE_INCISAL = 'targetcolor_incisal_value',
  TARGET_COLOR_CHROMA_MIDDLE = 'targetcolor_middle_chroma',
  TARGET_COLOR_CHROMA_CERVICAL = 'targetcolor_cervical_chroma',
  TARGET_COLOR_CHROMA_INCISAL = 'targetcolor_incisal_chroma',
  FRAMEWORK_OPTIONS = 'framework_thickness',
  FRAMEWORK_COLOR = 'framework_color',
  STAINING_BRAND = 'stainingbrand',
  BRAND = 'brand',
  AVAILABLE_SPACE = 'availspace',
  AVAILABLE_SPACE_MIDDLE = 'availspace_middle',
  AVAILABLE_SPACE_CERVICAL = 'availspace_cervical',
  AVAILABLE_SPACE_INCISAL = 'availspace_incisal',
}

export const validationSchema = () => {
  const { t } = useTranslation()

  const baseSchema = {
    [FORM_FIELDS.RECIPE_TYPE]: yup
      .string()
      .required(t('validation.recipeOption')),
    [FORM_FIELDS.RESTORATION_METHOD]: yup
      .string()
      .required(t('validation.restorationMethod')),
    [FORM_FIELDS.STUMP_SHADE]: yup
      .string()
      .required(t('validation.stumpShade')),
    [FORM_FIELDS.PREPARATION_COLOR]: yup.string().optional(),
    [FORM_FIELDS.TARGET_COLOR]: yup
      .string()
      .required(t('validation.targetColor')),
    [FORM_FIELDS.FRAMEWORK_OPTIONS]: yup
      .string()
      .required(t('validation.framework')),
    [FORM_FIELDS.FRAMEWORK_COLOR]: yup
      .array()
      .of(yup.string().typeError('Must be a string').required())
      .required(t('validation.frameworkColor')),
    [FORM_FIELDS.STAINING_BRAND]: yup
      .string()
      .required(t('validation.stainingBrand')),
    [FORM_FIELDS.TARGET_COLOR_VALUE_MIDDLE]: yup
      .string()
      .required(t('validation.middleValue')),
    [FORM_FIELDS.TARGET_COLOR_CHROMA_MIDDLE]: yup
      .string()
      .required(t('validation.middleChroma')),
    [FORM_FIELDS.AVAILABLE_SPACE_MIDDLE]: yup
      .string()
      .required(t('validation.middleSpace')),

    [FORM_FIELDS.BRAND]: yup
      .string()
      .when(
        FORM_FIELDS.RESTORATION_METHOD,
        (rawMethod, schema: yup.StringSchema) => {
          const restorationMethod = Array.isArray(rawMethod)
            ? rawMethod[0]
            : rawMethod

          if (restorationMethod === RESTORATION_METHOD.LAYERING) {
            return schema.required(t('validation.ceramicBrand'))
          }

          return schema.optional()
        },
      ),
  }

  const fieldMap = {
    [FORM_FIELDS.TARGET_COLOR_VALUE_CERVICAL]: t('validation.cervicalValue'),
    [FORM_FIELDS.TARGET_COLOR_VALUE_INCISAL]: t('validation.incisalValue'),
    [FORM_FIELDS.TARGET_COLOR_CHROMA_CERVICAL]: t('validation.cervicalChroma'),
    [FORM_FIELDS.TARGET_COLOR_CHROMA_INCISAL]: t('validation.incisalChroma'),
    [FORM_FIELDS.AVAILABLE_SPACE_CERVICAL]: t('validation.cervicalSpace'),
    [FORM_FIELDS.AVAILABLE_SPACE_INCISAL]: t('validation.incisalSpace'),
  }

  const regionFields = Object.keys(fieldMap) as FORM_FIELDS[]

  const createRegionValidation = (field: FORM_FIELDS) =>
    yup
      .string()
      .when(
        FORM_FIELDS.RECIPE_TYPE,
        (rawRecipeType, schema: yup.StringSchema) => {
          const recipeType = Array.isArray(rawRecipeType)
            ? rawRecipeType[0]
            : rawRecipeType

          if (recipeType === RECIPE_TYPE.TRIPLE_ZONE) {
            return schema.required(fieldMap[field]!)
          }

          return schema.optional()
        },
      )

  const regionSchema = regionFields.reduce(
    (acc, field) => {
      acc[field] = createRegionValidation(field)
      return acc
    },
    {} as Record<FORM_FIELDS, yup.StringSchema>,
  )
  return yup.object().shape({
    ...baseSchema,
    ...regionSchema,
  })
}
